<template>
	<el-row>
		<el-col :span="24" style="text-align: left;">
			<div class="base-form header">
				<h2>Марки автомобилей</h2>		
				<el-button type="primary" :icon="circleIcon" size="large" @click="onAddVehicleManufacturerClick">
					Новая марка автомобиля
				</el-button>
			</div>
		</el-col>
	</el-row>

	<el-row>
		<el-col :span="4">			
			<div class="base-form filter">
				<el-input
					v-model="tableFilterSearchQuery"	
					:readonly="tableLoading"
					clearable	
					max="250"			
					placeholder="Поиск марки автомобиля по названию">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</div>
		</el-col>		
	</el-row>

	<el-row>
		<el-col :span="24">
			<el-table
				row-key="id" 
				:data="tableData"
				v-loading="tableLoading"
				sortable="custom"				
				:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"
				@sort-change="onTableSortingChange"
				@row-dblclick="onTableRowDblClick">
				<template #empty>
					<el-text type="info">Список марок автомобилей пуст ☹️</el-text>
				</template>
				<el-table-column v-if="isPickerMode" width="100">
					<template #default="scope">
						<el-button
							size="small"
							type="primary"
							@click="(e) => onTablePickVehicleManufacturerClick(scope.row, e)">
							Выбрать
						</el-button>
					</template>
				</el-table-column>
				<el-table-column label="Марка" prop="name" sortable>
					<template #default="scope">
						<el-link 
							:underline="false" 
							type="primary"
							@click="(e) => onTableVehicleManufacturerCellClick(scope.row, e)">							
							{{ scope.row.name }}
						</el-link>
					</template>
				</el-table-column>
				<el-table-column>
					<template #default="scope">
						<div style="text-align: right;">
							<el-button
								:icon="deleteIcon"
								size="small"
								type="danger"
								circle
								@click="(e) => onTableDeleteVehicleManufacturerClick(scope.row, e)">
							</el-button>
						</div>
					</template>
				</el-table-column>
			</el-table>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import store from '@/store';
import { CirclePlus, Check, Search, Delete } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElMessageBox, ElNotification } from 'element-plus';
import TabsDictionary from '@/variables/tabs';
import EventHelper from '@/helpers/eventsHelper';
import PickerConfigurationModel from '@/models/PickerConfigurationModel';
import ApplicationTab from '@/models/ApplicationTab';
import { watch, WatchStopHandle } from 'vue';
import VehicleManufacturersApiHelper from '@/helpers/api/VehicleManufacturersApiHelper';
import VehicleManufacturer from '@/models/VehicleManufacturer';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElTag, ElLink
	}, 
	props: {
		params: Array
	},
})
export default class VehicleManufacturersView extends Vue {
	/** constants */		
	circleIcon = CirclePlus;
	deleteIcon = Delete;
	searchIcon = Search;
	checkIcon = Check;
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	reloadTableEvent = 'vehicleManufacturersListUpdate';
	vehicleManufacturersApiHelper = new VehicleManufacturersApiHelper(store.getters.jwt);

	/** props */	
	params!: Array<any>;

	/** view-models */
	tableLoading = false;	
	tableSortColumn = 'name';
	tableSortOrder = 'ascending';
	tableFilterSearchQuery: string | null = null;

	isPickerMode = false;
	onPickedCallback: Function | null = null;
	tableData: Array<VehicleManufacturer> = [];

	tableFilterSearchQueryWatcher?: WatchStopHandle;

	mounted() {
		this.tableFilterSearchQueryWatcher = watch(
			() => this.tableFilterSearchQuery,
			() => this.fetchVehicleManufacturersAsync()
		);

		const pickerModeArgument = this.params?.length ? this.params[0] as PickerConfigurationModel : null;
		
		/** check if picker mode activated */
		if (pickerModeArgument && 
			pickerModeArgument.isPickerModeEnabled === true &&
			pickerModeArgument.onItemPickedCallback) {
			this.isPickerMode = true;
			this.onPickedCallback = pickerModeArgument.onItemPickedCallback;
		}

		EventHelper.setEventListener(this.reloadTableEvent, this.fetchVehicleManufacturersAsync);
		EventHelper.dispatchEvent(this.reloadTableEvent);
	}

	unmounted() {
		if (this.tableFilterSearchQueryWatcher) {
			this.tableFilterSearchQueryWatcher();
		}

		EventHelper.removeEventListener(this.reloadTableEvent, this.fetchVehicleManufacturersAsync);
	}

	onTableSortingChange(eventObject: any) {
		this.tableSortColumn = eventObject.prop;
		this.tableSortOrder = eventObject.order;
		EventHelper.dispatchEvent(this.reloadTableEvent);
	}
	
	onTableRowDblClick(vehicleManufacturer: VehicleManufacturer) {
		const orderViewTab = TabsDictionary.vehicleManufacturerView;

		orderViewTab.params = [ vehicleManufacturer, true ];
		orderViewTab.allowMultiple = true;
		store.commit('addTab', orderViewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onTableVehicleManufacturerCellClick(vehicleManufacturer: VehicleManufacturer, e: Event) {
		e.stopPropagation();

		const orderViewTab = TabsDictionary.vehicleManufacturerView;
		orderViewTab.params = [ vehicleManufacturer, true ];
		orderViewTab.allowMultiple = true;
		store.commit('addTab', orderViewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onTableDeleteVehicleManufacturerClick(vehicleManufacturer: VehicleManufacturer, e: Event) {
		e.stopPropagation();
			
		this.vehicleManufacturersApiHelper
			.isDeletableAsync(vehicleManufacturer.id)
			.then(deletable => {
				if (!deletable) {
					ElMessageBox.confirm(
						'Удаление невозможно, у марки есть заказ-наряды или связанные модели', {
						confirmButtonText: 'OK',
						type: 'error',
					});
				} else {
					ElMessageBox.confirm(
						`Марка ${vehicleManufacturer.name} будет удалена. Продолжить ?`, {
						confirmButtonText: 'OK',
						cancelButtonText: 'Отмена',
						type: 'warning',
					})
					.then(() => this.deleteVehicleManufacturerAsync(vehicleManufacturer.id));
				}
		});
	}

	async fetchVehicleManufacturersAsync() {
		try {
			this.tableLoading = true;
			const vehicleManufacturers = await this.vehicleManufacturersApiHelper.getManufacturersAsync(
				this.tableSortColumn,
				this.tableSortOrder,
				this.tableFilterSearchQuery
			);
			if (!vehicleManufacturers) {
				ElNotification({
					message: 'Ошибка загрузки списка марок автомобилей',
					type: 'error',
				});				
			} else {
				this.tableData = vehicleManufacturers!;
			}
		} finally {
			this.tableLoading = false;
		}
	}

	async deleteVehicleManufacturerAsync(vehicleModelId: number) {		
		this.tableLoading = true;
		const isDeleted = await this.vehicleManufacturersApiHelper.deleteAsync(vehicleModelId);
		if (!isDeleted) {
			ElNotification({
				message: 'Ошибка удаление марки автомобиля',
				type: 'error',
			});
			return;
		}

		EventHelper.dispatchEvent(this.reloadTableEvent);
	}

	onAddVehicleManufacturerClick() {
		const appTab = TabsDictionary.vehicleManufacturerView;
		appTab.params = null;
		store.commit('addTab', appTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	/** handles callback and passes selected vehicle manufacturer to a parent component (if picker-mode is enabled) */	
	onTablePickVehicleManufacturerClick(vehicleManufacturer: VehicleManufacturer, e: Event) {
		e.stopPropagation();
		if (this.onPickedCallback) {
			this.onPickedCallback(vehicleManufacturer);
		}
	}
}
</script>